import React from "react";
import { Grid, Typography, Box, IconButton } from "@mui/material";
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageMenu from '../LanguageMenu.jsx';

export default function HomeFooter({ navigation, brandName, theme }) {
  const settings = useSelector(state => state.settingsdata.settings);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const currentLang = i18n.language;
  const fallbackName = (currentLang === 'am' || currentLang === 'ti') ? 'ፕሉክ' : (settings?.appName || 'Plukk');
  const footerBrandName = brandName || fallbackName;

  const footerTheme = theme?.footer || {};
  const footerMode = footerTheme.mode || 'dark';
  const isDark = footerMode !== 'light';
  const bg = footerTheme.bg || (isDark ? '#000' : '#fff');
  const text = footerTheme.text || (isDark ? '#fff' : '#0b0b0b');
  const muted = footerTheme.muted || (isDark ? 'rgba(255,255,255,0.7)' : '#475569');
  const linkColor = footerTheme.link || muted;
  const divider = footerTheme.divider || (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.10)');

  const footerLinks = {
    company: navigation?.footerCompany || [
      { label: 'about_us', path: '/about-us', visible: true },
      { label: 'contact_us', path: '/contact-us', visible: true },
      { label: 'privacy_policy', path: '/privacy-policy', visible: true },
    ],
    products: navigation?.footerProducts || [
      { label: 'ride', path: '/', visible: true },
      { label: 'drive', path: '/login', visible: true },
      { label: 'shop', path: '/shop', visible: true },
    ],
    legal: navigation?.footerLegal || [
      { label: 'privacy', path: '/privacy-policy', visible: true },
      { label: 'terms', path: '/privacy-policy', visible: true },
    ],
  };

  const visibleLinks = (links) => links.filter((item) => item.visible !== false);

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: bg,
        color: text,
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 6 },
      }}
      style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <Grid container spacing={4}>
          {/* Logo & Social */}
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: -0.5,
                mb: 2,
                fontFamily: '"UberMove", -apple-system, sans-serif',
              }}
            >
              {footerBrandName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {settings?.FacebookHandle && (
                <IconButton
                  component="a"
                  href={settings.FacebookHandle}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  sx={{ color: text, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)' } }}
                >
                  <FacebookIcon />
                </IconButton>
              )}
              {settings?.TwitterHandle && (
                <IconButton
                  component="a"
                  href={settings.TwitterHandle}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                  sx={{ color: text, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)' } }}
                >
                  <TwitterIcon />
                </IconButton>
              )}
              {settings?.InstagramHandle && (
                <IconButton
                  component="a"
                  href={settings.InstagramHandle}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  sx={{ color: text, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)' } }}
                >
                  <InstagramIcon />
                </IconButton>
              )}
              {settings?.LinkedInHandle && (
                <IconButton
                  component="a"
                  href={settings.LinkedInHandle}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{ color: text, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.06)' } }}
                >
                  <LinkedInIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                mb: 2,
              }}
            >
              {t('company') || 'Company'}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {visibleLinks(footerLinks.company).map((link, idx) => (
                <Box component="li" key={idx} sx={{ mb: 1.5 }}>
                  <Link
                    to={link.path}
                    style={{
                      color: linkColor,
                      textDecoration: 'none',
                      fontSize: 15,
                    }}
                  >
                    {t(link.label) || link.label}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Products Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                mb: 2,
              }}
            >
              {t('products') || 'Products'}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {visibleLinks(footerLinks.products).map((link, idx) => (
                <Box component="li" key={idx} sx={{ mb: 1.5 }}>
                  <Link
                    to={link.path}
                    style={{
                      color: linkColor,
                      textDecoration: 'none',
                      fontSize: 15,
                    }}
                  >
                    {t(link.label) || link.label}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Language */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                mb: 2,
              }}
            >
              {t('language') || 'Language'}
            </Typography>
            <LanguageMenu />
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: `1px solid ${divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: 14 }}>
            © {new Date().getFullYear()} {settings?.CompanyName || footerBrandName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {visibleLinks(footerLinks.legal).map((link, idx) => (
              <Link
                key={idx}
                to={link.path}
                style={{
                  color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
                  textDecoration: 'none',
                  fontSize: 14,
                }}
              >
                {t(link.label) || link.label}
              </Link>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
